/**
 * Centralized sound effect playback utility.
 * Handles playing sound effects with consistent volume levels and error handling.
 */

import { TILE_SIZE } from "../models/common";

interface SoundConfig {
  volume?: number;
}

interface SoundGlowConfig {
  duration?: number;
  radius?: number;
  color?: number;
}

const DEFAULT_VOLUMES: Record<string, number> = {
  "no-ammo-sound": 0.4,
  "weapon-reload": 0.5,
  bandage: 0.5,
  heal: 0.5,
  throw: 0.4,
  explosion: 0.4,
  teleport: 0.5,
  highlight: 0.3,
  "loading-complete": 0.5,
  "unarmed-attack": 0.3,
  hit: 0.4,
  airdrop: 0.5,
  "pick-item-sound": 0.5,
  "teleport-appear": 0.4,
  "menu-click-sound": 0.4,
  "shop-sound": 0.5,
  "open-container-sound": 0.5,
  "close-container-sound": 0.5,
  "inventory-open": 0.5,
  "extraction-success": 0.4,
  "extraction-failed": 0.4,
  sprint: 0.4,
  step: 0.5,
};

/**
 * Show a visual glow indicator on the screen edge in the direction of a sound source.
 * The glow fades out over the specified duration.
 *
 * @param scene - The Phaser scene to render the glow in
 * @param soundTileX - The tile X coordinate of the sound source
 * @param soundTileY - The tile Y coordinate of the sound source
 * @param config - Configuration for the glow appearance and duration
 */
function showSoundGlowIndicator(
  scene: Phaser.Scene,
  soundTileX: number,
  soundTileY: number,
  config: SoundGlowConfig = {},
): void {
  const { duration = 2000, radius = 100, color = 0xffff00 } = config;

  // Get camera and player position
  const camera = scene.cameras.main;
  if (!camera) return;

  const playerTileX = Math.floor(window.player.x / TILE_SIZE);
  const playerTileY = Math.floor(window.player.y / TILE_SIZE);

  // Calculate direction from player to sound source
  const dx = soundTileX - playerTileX;
  const dy = soundTileY - playerTileY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  if (distance === 0) return; // Sound is at player position

  // Check if sound source is visible on screen
  const soundWorldX = soundTileX * TILE_SIZE;
  const soundWorldY = soundTileY * TILE_SIZE;
  const cameraX = camera.scrollX;
  const cameraY = camera.scrollY;
  const visibleWidth = camera.width / camera.zoom;
  const visibleHeight = camera.height / camera.zoom;

  if (
    soundWorldX >= cameraX &&
    soundWorldX <= cameraX + visibleWidth &&
    soundWorldY >= cameraY &&
    soundWorldY <= cameraY + visibleHeight
  ) {
    // Sound source is visible on screen, no need for glow indicator
    return;
  }

  // Normalize direction
  const dirX = dx / distance;
  const dirY = dy / distance;

  // Position glow on screen edge in the direction of the sound
  const cameraWidth = camera.width;
  const cameraHeight = camera.height;
  const centerX = cameraWidth / 2;
  const centerY = cameraHeight / 2;

  // Find intersection with screen edge
  const absX = Math.abs(dirX);
  const absY = Math.abs(dirY);

  let maxDist: number;
  if (absX > absY) {
    // Hit left or right edge
    maxDist = centerX / absX;
  } else {
    // Hit top or bottom edge
    maxDist = centerY / absY;
  }

  // Position glow at the edge of screen
  const glowX = centerX + dirX * maxDist;
  const glowY = centerY + dirY * maxDist;

  // Scale radius based on distance - closer sounds have larger radius
  // Max distance is 15 tiles, so normalize distance and inverse it
  const maxHearingDistance = 15;
  const distanceFactor = Math.max(0.3, 1 - distance / maxHearingDistance);
  const scaledRadius = radius * distanceFactor;

  // Create the glow graphics object
  const glow = scene.add.graphics();
  glow.setScrollFactor(0); // Fixed to camera, not world

  // Draw outer circle (slightly transparent)
  glow.fillStyle(color, 0.3);
  glow.fillCircle(glowX, glowY, scaledRadius * 1.3);

  // Draw inner circle (more opaque)
  glow.fillStyle(color, 0.7);
  glow.fillCircle(glowX, glowY, scaledRadius);

  // Create fade-out tween
  scene.tweens.add({
    targets: glow,
    alpha: 0,
    duration: duration,
    ease: "Quad.easeOut",
    onComplete: () => {
      glow.destroy();
    },
  });
}

/**
 * Play a sound effect in the given scene.
 * Uses predefined default volumes if not specified.
 *
 * @param scene - The Phaser scene to play the sound in
 * @param soundName - The name of the sound to play
 * @param customVolume - Optional custom volume override (0-1)
 */
export function playSound(
  scene: Phaser.Scene,
  soundName: string,
  geo: {
    tileX: number;
    tileY: number;
  } | null = null,
  customVolume?: number,
): void {
  if (!scene || !scene.sound) {
    console.warn(`[playSound] Invalid scene or no sound manager available`);
    return;
  }

  if (localStorage.getItem("sfxEnabled") === "false") {
    return;
  }

  if (!scene.cache.audio.exists(soundName)) {
    console.warn(`[playSound] Sound "${soundName}" not found in cache`);
    return;
  }

  let volume =
    customVolume !== undefined
      ? customVolume
      : (DEFAULT_VOLUMES[soundName] ?? 0.5);

  // Adjust volume based on distance to player if geo is provided
  if (geo !== null) {
    const playerTileX = Math.floor(window.player.x / TILE_SIZE);
    const playerTileY = Math.floor(window.player.y / TILE_SIZE);

    // Calculate distance between sound source and player
    const distance = Phaser.Math.Distance.Between(
      geo.tileX,
      geo.tileY,
      playerTileX,
      playerTileY,
    );
    const maxHearingDistance = 25;
    if (distance > maxHearingDistance) {
      // Too far to hear the sound
      return;
    }
    // Simple linear falloff
    const distanceVolumeFactor = 1 - distance / maxHearingDistance;
    volume = volume * distanceVolumeFactor;

    // Show visual glow indicator on screen edge
    showSoundGlowIndicator(scene, geo.tileX, geo.tileY);
  }

  try {
    scene.sound.play(soundName, { volume });
  } catch (error) {
    console.error(`[playSound] Failed to play sound "${soundName}":`, error);
  }
}
